import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { useFormContext, Controller } from 'react-hook-form';
import { useTheme } from '@mui/system';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import InputField from '#component/form/input';
import DateTimeField from '#component/form/date-time-field';
import LocationMap from '#component/form/location-map';
import RHFTimeFields from '#component/form/rhf/rhf-time-fields';
import SwitchView from '#component/general/switch-view';
import {
  DEFAULT_DISTANCE_DEVIATION,
  getDistanceDeviationOptions
} from '#component/shipment/form/delivery/distance-deviation';
import AutocompleteServiceField, { renderAutocompleteOption } from '#component/general/autocomplete-service-field';
import { useAddressbookGoogleOptionsService, useOptionsService } from '#service/autocomplete-service/hooks';
import { AddressbookOptionService } from '#service/autocomplete-service/service';
import AddressbookDialogButton from '#component/shipment/form/delivery/timeline/addressbookDialog/addressbookDialogButton';
import {
  isPristineForm,
  omitValues,
  Cleaner,
  withAddressbookContactId,
  mapDeliveryInitialValues
} from '#component/shipment/form/helper';
import { setDisplayFields as setDisplayFieldsAction } from '#action/shipment/delivery';
import { renderCompanyOption } from './autocomplete';
import {
  StyledDateTimeField,
  ContactCnt,
  companyFieldStyle,
  FieldsCnt,
  AddressLocationCnt,
  AddressCnt,
  addressFieldStyle,
  DeviationFields,
  ButtonGroupCnt,
  buttonGroupStyle,
  GeofenceCnt,
  GeofenceTitle,
  geofenceRadioControlStyle,
  radioLabelStyle,
  TimeFieldsContainer,
  TimeDeviationCnt,
  DeviationFieldTitle,
  TimeFieldsTextError
} from './style';
import Geolocation from './geolocation';
import { formattedAddress, convertDistance } from '#helper';

const addressFields = [
  { id: 1, name: 'address1', label: 'Address 1' },
  { id: 2, name: 'address2', label: 'Address 2' },
  { id: 3, name: 'city', label: 'City' },
  { id: 4, name: 'state', label: 'State' },
  { id: 5, name: 'country', label: 'Country' },
  { id: 6, name: 'zipCode', label: 'Zip code' }
];

const companyFields = [
  { id: 1, name: 'companyName', label: 'Company name', optionServiceArgs: ['company'], isCompanyField: true },
  { id: 2, name: 'firstName', label: 'First name', optionServiceArgs: ['firstName'] },
  { id: 3, name: 'lastName', label: 'Last name', optionServiceArgs: ['lastName'] }
];

const State = props => {
  const {
    index,
    member,
    state = {},
    state: { id: stateId } = {},
    disabled,
    handleOpenAddressbookDialog
  } = props;
  const theme = useTheme();
  const dispatch = useDispatch();
  const methods = useFormContext();
  const { control, setValue, watch, setError, getFieldState, formState } = methods || {};

  const account = useSelector(s => s.account, shallowEqual);
  const measurementSystem = account?.measurementSystem;
  const justSelectedTab = useSelector(s => s.shipmentForm.justSelectedTab);

  const statesValues = watch?.('states') || [];
  const currentValues = statesValues[index] ?? {};
  const timeDeviationError = getFieldState?.(`${member}.minutes`, formState).error?.message;

  const radiusFieldValue = +(currentValues.radius ?? convertDistance(DEFAULT_DISTANCE_DEVIATION, 'metric', measurementSystem));
  const metricRadiusValue = convertDistance(radiusFieldValue, measurementSystem, 'metric');
  const radiusOptions = useMemo(
    () => getDistanceDeviationOptions(radiusFieldValue, measurementSystem),
    [radiusFieldValue, measurementSystem]
  );

  const [tab, setTab] = useState(0);
  const mapExists = !!document.getElementById(`map_id_${stateId}`);

  const displayFields = useSelector(s => s.delivery, shallowEqual);
  const stateAddressbookId = displayFields[index]?.addressbookContactId;

  const initialValues = useMemo(
    () => (state ? mapDeliveryInitialValues([state], measurementSystem)[0] : {}),
    [state, measurementSystem]
  );

  const [autocompleteAddressbookId, setAutocompleteAddressbookId] = useState(null);

  const currentFormValues = omitValues(Cleaner.cleanObject(currentValues));
  const initialFormValues = omitValues(Cleaner.cleanObject(initialValues));
  const isPristine = isPristineForm(currentFormValues, initialFormValues);

  const DEFAULT_GEOFENCE_DIRECTION = 1;
  const [geofenceDirection, setGeofenceDirection] = useState(DEFAULT_GEOFENCE_DIRECTION);

  useEffect(() => {
    if (Number.isInteger(state.geofenceDirection)) {
      setGeofenceDirection(state.geofenceDirection);
    }
  }, [state.geofenceDirection]);

  const updateFormFields = optionDetails => {
    const radius = optionDetails.radius ?
      convertDistance(optionDetails.radius, 'metric', measurementSystem) :
      convertDistance(DEFAULT_DISTANCE_DEVIATION, 'metric', measurementSystem);

    setValue?.(`${member}.formattedAddress`, formattedAddress(optionDetails.address));
    setValue?.(`${member}.locations`, optionDetails.locations);
    setValue?.(`${member}.address`, optionDetails.address);
    setValue?.(`${member}.radius`, radius);

    setAutocompleteAddressbookId(optionDetails.addressbookContactId);
    dispatch(
      setDisplayFieldsAction(
        withAddressbookContactId(displayFields, index, optionDetails.addressbookContactId)
      )
    );

    if (optionDetails.minutes) {
      setValue?.(`${member}.minutes.hour`, 1);
    }

    companyFields.forEach(({ name }) => {
      if (Object.prototype.hasOwnProperty.call(optionDetails, name)) {
        setValue?.(`${member}.company.${name}`, optionDetails[name] ?? null);
      }
    });
  };

  const changeTab = newTab => () => setTab(newTab);

  const handleChange = forceChange => {
    if (autocompleteAddressbookId || stateAddressbookId || forceChange) {
      setAutocompleteAddressbookId(null);
      const omitAddressbookContactId = displayFields.map((d, i) => {
        if (i === index && Object.prototype.hasOwnProperty.call(d, 'addressbookContactId')) {
          const { addressbookContactId, ...rest } = d;
          return rest;
        }
        return d;
      });
      dispatch(setDisplayFieldsAction(omitAddressbookContactId));
    }
  };

  const handleOpenAddContactDialog = (openedIndex, isDeliveryFormPristine) => {
    const formattedAddr = currentValues.formattedAddress;
    const hasLocation = Boolean(
      formattedAddr && (typeof formattedAddr !== 'string' || formattedAddr.trim())
    );

    if (!hasLocation) {
      setError?.(`${member}.formattedAddress`, {
        type: 'manual',
        message: 'Location should be provided'
      });
      return;
    }

    handleOpenAddressbookDialog(openedIndex, isDeliveryFormPristine);
  };

  const customChange = (field, value) => {
    setValue?.(field, value);
    handleChange(true);
  };

  const isDisabledSaveToContacts = stateAddressbookId ||
    (initialValues?.addressbookContactId && isPristine);

  const handleChangeGeofence = e => {
    const direction = +e.target.value;
    setGeofenceDirection(direction);
    setValue?.(`${member}.geofenceDirection`, direction);
  };

  if (!state) {
    return null;
  }

  return (
    <>
      {(state.date !== undefined || index === 0) && (
        <StyledDateTimeField>
          <Controller
            name={`${member}.date`}
            control={control}
            render={({ field, fieldState }) => (
              <DateTimeField
                {...field}
                meta={fieldState}
                label={index === 0 ? 'Start date' : 'Date'}
                fullWidth
                disabled={disabled}
              />
            )}
          />
        </StyledDateTimeField>
      )}
      {(state.locations || state.address || state.company) && (
        <>
          <ContactCnt name={`${member}.company`} onChange={handleChange}>
            {companyFields.map(field => (
              <AutocompleteServiceField
                key={field.id}
                name={`${member}.company.${field.name}`}
                style={field.isCompanyField ? companyFieldStyle : undefined}
                inputProps={{ label: field.label, variant: 'outlined' }}
                disabled={disabled}
                onOptionChange={updateFormFields}
                useOptionsService={useOptionsService}
                useOptionsServiceArgs={[field.optionServiceArgs]}
                useOptionServiceProvider={AddressbookOptionService}
                renderOption={renderCompanyOption(field.name)}
                loading={justSelectedTab}
              />
            ))}
          </ContactCnt>
          <AddressLocationCnt onChange={handleChange}>
            <FieldsCnt style={{ display: tab === 0 ? 'block' : 'none' }}>
              <AutocompleteServiceField
                name={`${member}.formattedAddress`}
                onOptionChange={updateFormFields}
                disabled={disabled}
                inputProps={{
                  label: 'Location',
                  variant: 'outlined'
                }}
                useOptionsService={useAddressbookGoogleOptionsService}
                renderOption={renderAutocompleteOption}
                GeolocationButton={Geolocation}
                loading={justSelectedTab}
              />
              {state.locations && (
                <>
                  <Controller
                    name={`${member}.locations`}
                    control={control}
                    render={({ field }) => (
                      <LocationMap
                        {...field}
                        id={stateId}
                        disabled={disabled}
                        radius={+metricRadiusValue}
                        change={customChange}
                        member={member}
                        measurementSystem={measurementSystem}
                        onChange={handleChange}
                      />
                    )}
                  />
                  {mapExists && (
                    <DeviationFields>
                      <Controller
                        name={`${member}.radius`}
                        control={control}
                        render={({ field, fieldState }) => (
                          <InputField
                            {...field}
                            meta={fieldState}
                            label="Distance deviation"
                            variant="outlined"
                            select
                            disabled={disabled}
                            onChange={e => {
                              field.onChange(e);
                              handleChange();
                            }}
                          >
                            {radiusOptions.map(option => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </InputField>
                        )}
                      />
                      {currentValues.date && (
                        <RHFTimeFields
                          name={`${member}.minutes`}
                          label="Time deviation"
                          disabled={disabled}
                          error={timeDeviationError}
                          Container={TimeFieldsContainer}
                          TimeDeviationCnt={TimeDeviationCnt}
                          DeviationFieldTitle={DeviationFieldTitle}
                          TextError={TimeFieldsTextError}
                        />
                      )}
                    </DeviationFields>
                  )}
                </>
              )}
            </FieldsCnt>
            <AddressCnt style={{ display: tab === 1 ? 'flex' : 'none' }}>
              {addressFields.map(field => {
                if (field.name === 'address1') {
                  return (
                    <AutocompleteServiceField
                      key={field.id}
                      style={addressFieldStyle(theme)}
                      name={`${member}.address.${field.name}`}
                      onOptionChange={updateFormFields}
                      disabled={disabled}
                      inputProps={{ label: field.label, variant: 'outlined' }}
                      useOptionsService={useAddressbookGoogleOptionsService}
                      renderOption={renderAutocompleteOption}
                      loading={justSelectedTab}
                    />
                  );
                }
                return (
                  <Controller
                    key={field.id}
                    name={`${member}.address.${field.name}`}
                    control={control}
                    render={({ field: controllerField, fieldState }) => (
                      <InputField
                        {...controllerField}
                        style={addressFieldStyle(theme)}
                        meta={fieldState}
                        label={field.label}
                        variant="outlined"
                        disabled={disabled}
                      />
                    )}
                  />
                );
              })}
            </AddressCnt>
            <ButtonGroupCnt>
              <AddressbookDialogButton
                index={index}
                handleOpenAddressbookDialog={handleOpenAddContactDialog}
                disabled={Boolean(isDisabledSaveToContacts)}
                isDeliveryFormPristine={isPristine}
              />
              <SwitchView style={buttonGroupStyle(theme)} tab={tab} changeTab={changeTab} />
            </ButtonGroupCnt>
          </AddressLocationCnt>
          <GeofenceCnt>
            <Typography component={GeofenceTitle}>Complete on:</Typography>
            <RadioGroup
              row
              aria-label="geofenceDirection"
              name="geofenceDirection"
              value={geofenceDirection}
              onChange={handleChangeGeofence}
            >
              <FormControlLabel
                value={1}
                control={<Radio style={geofenceRadioControlStyle(theme)} />}
                label="Arrival"
                style={radioLabelStyle}
                disabled={disabled}
              />
              <FormControlLabel
                value={0}
                control={<Radio style={geofenceRadioControlStyle(theme)} />}
                label="Departure"
                style={radioLabelStyle}
                disabled={disabled}
              />
            </RadioGroup>
          </GeofenceCnt>
        </>
      )}
    </>
  );
};

export default State;
